import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import authAPI from 'apis/authAPI';
import { LoginRequest } from 'types/auth';
import logo from "assets/images/LUCA_LOGO.png";

import Button from 'components/utils/Button';
import LoadingSpinner from 'components/utils/LoadingSpinner';
import { CUNEX_STAFF_AUTH_URL, CUNEX_STUDENT_AUTH_URL } from 'config';

const SignIn: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();


    setError('');
    setIsLoading(true);

    try {
      const loginRequest: LoginRequest = { userId: username, password };
      const { data, statusCode, error } = await authAPI.login(loginRequest);
      
      if (error || statusCode !== 200 || !data || !data.accessToken) {
        setError('Login failed');
      } else {
        localStorage.setItem('token', data.accessToken);
        // Redirect to the stored route or default to '/'
        const redirectPath = localStorage.getItem('redirectAfterLogin') || '/';
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
        return;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed: An unexpected error occurred';
      setError(`Login failed: ${errorMessage}`);
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-grad-pk-bl">
      <div className="flex flex-col md:flex-row bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full mx-4">
        {/* Logo Section */}
        <div className="flex items-center justify-center bg-gradient-to-br p-8 md:p-12">
          <div className="text-center">
            <img 
              src={logo} 
              alt="LUCA Logo"
              className="w-48 h-48 md:w-64 md:h-64 object-contain"
            />
          </div>
        </div>

        {/* Form Section */}
        <div className="flex-1 p-8 md:p-12">
          <h2 className="text-xl font-bold text-BADBlue mb-4 text-center">
            Sign In
          </h2>
          
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label 
                htmlFor="username" 
                className="block text-BADBlue text-base mb-2"
              >
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={isLoading}
                className="w-full p-3 text-BADBlue text-base border rounded-[6px] focus:outline-none focus:ring-2 focus:ring-BABlue disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label 
                htmlFor="password" 
                className="block text-BADBlue text-base mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="w-full p-3 text-BADBlue text-base border rounded-[6px] focus:outline-none focus:ring-2 focus:ring-BABlue disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter your password"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="show-password"
                checked={showPassword}
                onChange={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                className="w-4 h-4 text-BADBlue border-gray-300 rounded focus:ring-BADBlue focus:ring-2 disabled:cursor-not-allowed"
              />
              <label 
                htmlFor="show-password" 
                className="ml-2 text-sm text-BADBlue select-none cursor-pointer"
              >
                Show Password
              </label>
            </div>

            {isLoading ? (
              <div className="w-full h-[50px] rounded-[6px] flex items-center justify-center">
                <div className="scale-50">
                  <LoadingSpinner />
                </div>
              </div>
            ) : (
              <Button 
                text="Sign in" 
                width="w-full" 
                height="h-[50px]" 
                bgColor="bg-BADBlue" 
                fontColor="text-BAWhite"
                typeOfBtn="submit"
              />
            )}
          </form>

          <div className="my-6 flex items-center gap-3 text-sm text-gray-500">
            <div className="h-px flex-1 bg-gray-200" />
            <span>or</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => { window.location.href = CUNEX_STUDENT_AUTH_URL; }}
              className="h-[50px] w-full rounded-[6px] border border-BAMPink text-BAMPink hover:bg-BAMPink hover:text-BAWhite"
            >
              Sign in with CUNEX (Student)
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = CUNEX_STAFF_AUTH_URL; }}
              className="h-[50px] w-full rounded-[6px] border border-BADBlue text-BADBlue hover:bg-BADBlue hover:text-BAWhite"
            >
              Sign in with CUNEX (Staff)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
